<template>
  <v-container class="grey-lighten-3 full-height">
    <v-row justify="center">
      <v-col cols="12" xl="10">
        <v-sheet class="report-paper pa-6" elevation="3" id="printable-area">

          <!-- WATERMARK -->
          <div class="watermark" v-if="budgetDetails?.branch?.company?.watermark && invoiceHeader">
            <img :src="`${getFileServerUrl}${budgetDetails?.branch?.company?.watermark}`" alt="">
          </div>

          <div class="invoice-container">
            <div class="invoice-content">
              <table style="width: 100%; border-collapse: collapse;">
                
                <!-- HEADER IMAGE -->
                <thead>
                  <tr v-if="budgetDetails?.branch?.company?.header_image && invoiceHeader">
                    <td colspan="100%">
                      <img :src="`${getFileServerUrl}${budgetDetails?.branch?.company?.header_image}`" style="width: 100%;" alt="">
                    </td>
                  </tr>
                </thead>

                <tbody>
                  <!-- DEFAULT HEADER -->
                  <tr v-if="!budgetDetails?.branch?.company?.header_image && invoiceHeader">
                    <td colspan="100%">
                      <div class="page-header">
                        <div class="header-section d-flex justify-space-between align-start">
                          <div class="company-info">
                            <h4 class="company-name">{{ budgetDetails?.branch?.company?.name || '' }}</h4>
                            <p class="company-text">{{ budgetDetails?.branch?.company?.address || '' }}</p>
                            <p class="company-text">
                              <v-icon size="small">mdi-phone-outline</v-icon>
                              {{ budgetDetails?.branch?.company?.mobile || '' }}
                            </p>
                            <p class="company-text">
                              <v-icon size="small">mdi-email-outline</v-icon>
                              {{ budgetDetails?.branch?.company?.email || '' }}
                            </p>
                          </div>

                          <div class="logo-container">
                            <v-img
                              :src="`${getFileServerUrl}/${budgetDetails?.branch?.company?.logo || 'uploads/demo_images/user.png'}`"
                              max-width="150"
                              max-height="70"
                              contain
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <!-- TITLE -->
                  <tr>
                    <td colspan="100%">
                      <div class="text-center my-6">
                        <h2 class="report-title">BUDGET RELEASE DETAILS</h2>
                      </div>
                    </td>
                  </tr>

                  <!-- MASTER INFO BOX -->
                  <tr class="master-box-parent">
                    <td colspan="100%">
                      <div class="master-box mt-4" style="font-size: 9px;">
                        <table class="dep-table table-borderd" style="width:100%">
                          <tbody>
                            <tr>
                              <td width="40%"><strong>Budget Code</strong></td>
                              <td width="60%">{{ budgetDetails?.code }}</td>
                            </tr>
                            <tr>
                              <td><strong>Financial Year</strong></td>
                              <td>{{ budgetDetails?.financial_year }}</td>
                            </tr>
                            <tr>
                              <td><strong>Release Date</strong></td>
                              <td>{{ budgetDetails?.release_date }}</td>
                            </tr>
                            <tr>
                              <td><strong>Department</strong></td>
                              <td>{{ budgetDetails?.department?.name }}</td>
                            </tr>
                            <tr>
                              <td><strong>Description</strong></td>
                              <td>{{ budgetDetails?.description }}</td>
                            </tr>
                            <tr>
                              <td><strong>Status</strong></td>
                              <td>{{ budgetDetails?.status == 1 ? 'Published' : 'Draft' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>

                  <!-- DETAILS TABLE -->
                  <tr>
                    <td colspan="100%" class="details-table mt-6">
                      <table class="dep-table mt-3 table-borderd">
                        <thead>
                          <tr>
                            <th>Budget Type</th>
                            <th>Budget Head</th>
                            <th>Sub Head</th>
                            <th>Budget Amount</th>
                            <th>Previous Released</th>
                            <th>Unused Budget</th>
                            <th>Released Amount</th>
                            <th>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="detail in budgetDetails?.details" :key="detail.id">
                            <td>{{ detail.budget_sub_head?.budget_head?.budget_type?.name }}</td>
                            <td>{{ detail.budget_sub_head?.budget_head?.name }}</td>
                            <td>{{ detail.budget_sub_head?.name }}</td>
                            <td class="text-right">{{ formatNumber(detail.budget_amount) }}</td>
                            <td class="text-right">{{ formatNumber(detail.previous_released_amount) }}</td>
                            <td class="text-right">{{ formatNumber(detail.unused_budget_amount) }}</td>
                            <td class="text-right">{{ formatNumber(detail.released_amount) }}</td>
                            <td class="text-right">{{ formatNumber(detail.budget_balance) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>

                </tbody>

                <tfoot>
                  <tr>
                    <td colspan="100%">
                      <div style="height: 100px;"></div>
                    </td>
                  </tr>
                </tfoot>

              </table>
            </div>
          </div>

        </v-sheet>
      </v-col>
    </v-row>

    <!-- PRINT BUTTON -->
    <v-row class="d-print-none mt-4">
        <v-col cols="12" class="d-flex justify-center">
            <v-btn color="primary" prepend-icon="mdi-printer" @click="print(0)">
            Print without pad
            </v-btn>

            <v-btn color="primary" class="ml-2" prepend-icon="mdi-printer" @click="print(1)">
            Print with pad
            </v-btn>
        </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

const store = useStore();
const route = useRoute();

const id = route.params.id;
const budgetDetails = ref(null);
const invoiceHeader = ref(true);
const pagePadding = ref(0);

const getFileServerUrl = computed(() => store.getters.getFileServerUrl);

const formatNumber = (val) => {
  if (val == null) return '';
  return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const print = (isPad) => {
  invoiceHeader.value = isPad === 1;
  pagePadding.value = isPad === 1 ? 0 : 100;

  setTimeout(() => {
    const printContent = document.getElementById("printable-area").outerHTML;
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Budget Details</title>
          <style>
            body { margin:0; font-family:sans-serif; }
            table { width:100%; border-collapse: collapse; }
            th, td { border:1px solid #000; padding:4px; font-size:10px; }
            th { background:#eee; }
            @page { size:A4; margin:${pagePadding.value}px 10px; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);

  }, 300);
};

onMounted(async () => {
  try {
    const res = await store.dispatch("getBudgetReleaseById", id);
    if (res?.success) budgetDetails.value = res.data;
  } catch (error) {
    console.error(error);
  }
});
</script>

<style scoped>
.full-height { min-height: 60vh; }
.grey-lighten-3 { background-color: #eeeeee; }
.report-paper { background: white; }

.company-name { font-size: 9px; font-weight: bold; }
.company-text { font-size: 9px; }

.report-title {
  font-size: 16px;
  text-align: center;
}

.dep-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9px;
}

.dep-table th,
.dep-table td {
  border: 1px solid #000;
  padding: 3px;
}

.text-center { text-align: center; }
.text-right { text-align: right; }

.watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  opacity: 0.1;
  transform: translate(-50%, -50%);
}

.d-print-none { display: block; }

@media print {
  .d-print-none { display: none; }
}
</style>